import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageBackground, ScrollView, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RouteProp, useNavigation } from '@react-navigation/native';

import BackArrow from '@/assets/icons/backArrow';
import InfoIcon from '@/assets/icons/info';
import PracticeIcon from '@/assets/icons/practiceIcon';
import { CashBalanceButton } from '@/components/BalanceButton';
import RichText, { ParsedTypes } from '@/components/RichText';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { isMediaFilled } from '@/data/prismicClient';
import { BetrBucksToggle } from '@/feature/game-details-arcade/components/BetrBucksToggle';
import { useWallet } from '@/hooks/use-wallet';
import { ArcadeLobbyStackParamList } from '@/navigation/arcade/types';
import { designSystem } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { EntryRadialSlider } from '../components/EntryRadialSlider';
import { useGameHowToPlay } from '../components/HowToPlay/GameHowToPlayProvider';
import { ModeSwitch, Modes } from '../components/ModeSwitch';
import { PLAY_BAR_HEIGHT } from '../components/PlayBar';
import { useGameSettingsStore } from '../hooks/use-game-settings';
import { useArcadeGameHowToPlayContent } from '../hooks/use-how-to-play-content';

const NavButton = ({ onPress, children }: { onPress: () => void; children: React.ReactNode }) => {
    return (
        <TouchableOpacity onPress={onPress}>
            <Box
                style={styles.shadow}
                backgroundColor={ds.colorToken.background.neutral.base}
                borderWidth={1}
                borderColor={ds.colorToken.border.strong}
                height={35}
                width={35}
                borderRadius="r36"
                alignItems="center"
                justifyContent="center"
            >
                {children}
            </Box>
        </TouchableOpacity>
    );
};

const BackButton = () => {
    const navigation = useNavigation();
    return (
        <NavButton onPress={() => navigation.goBack()}>
            <BackArrow color={ds.colorValue.icon.primary} />
        </NavButton>
    );
};

const InfoButton = ({ howToPlayUid }: { howToPlayUid?: string }) => {
    const { showGameHowToPlay } = useGameHowToPlay();
    const { data } = useArcadeGameHowToPlayContent(howToPlayUid ?? '');

    const howToPlayContent = useMemo(() => {
        if (!data) {
            return undefined;
        }

        return {
            howToPlay: data.data.sections
                .filter(item => !!item.title)
                .map(item => {
                    const image = isMediaFilled(item.image) ? item.image.url : undefined;
                    return {
                        title: String(item.title),
                        image,
                        content: (
                            <RichText
                                data={item.content}
                                typeProps={{
                                    [ParsedTypes.P]: {
                                        color: 'neutral300',
                                        variant: 'bodyMedium2',
                                        textAlign: 'left',
                                    },
                                }}
                            />
                        ),
                    };
                }),
            scoring: data.data.scoring
                .filter(item => !!item.action)
                .map(item => ({
                    action: String(item.action),
                    points: String(item.points),
                    isNegative: item.is_negative,
                })),
            howToMaster: data.data.how_to_master_sections
                .filter(item => !!item.title)
                .map(item => {
                    const image = isMediaFilled(item.image) ? item.image.url : undefined;
                    return {
                        title: String(item.title),
                        image,
                        content: (
                            <RichText
                                data={item.content}
                                typeProps={{
                                    [ParsedTypes.P]: {
                                        color: 'neutral300',
                                        variant: 'bodyMedium2',
                                        textAlign: 'left',
                                    },
                                }}
                            />
                        ),
                    };
                }),
        };
    }, [data]);

    const onPress = () => {
        if (!howToPlayContent) {
            return;
        }
        showGameHowToPlay(howToPlayContent);
    };

    return (
        <NavButton onPress={onPress}>
            <InfoIcon color={ds.colorValue.icon.primary} />
        </NavButton>
    );
};

const HEADER_HEIGHT = 230;

export const ArcadeGameDetailsScreen = ({
    route,
}: {
    route: RouteProp<ArcadeLobbyStackParamList, 'ArcadeGameDetailsScreen'>;
}) => {
    const { t } = useTranslation('arcade_game_details');
    const { game } = route.params;
    const [mode, setMode] = useState<Modes>('1v1');
    const { top, bottom } = useSafeAreaInsets();
    const { betrBucks } = useWallet();

    const setGameSettings = useGameSettingsStore(state => state.actions.setGameSettings);
    const selectedStake = useGameSettingsStore(state => state.selectedStake);
    const useBetrBucks = useGameSettingsStore(state => state.useBetrBucks);

    const stakeCurrency = useBetrBucks ? 'FRE' : 'USD';

    const stakes = useMemo(() => {
        const modeStakes = (game.mode_stakes['1v1'] ?? []).filter(stake => stake.currency === stakeCurrency);
        return [{ stake: 0, rake: 0, currency: stakeCurrency }, ...modeStakes];
    }, [game.mode_stakes, stakeCurrency]);

    const hasVersusStakes = stakes.length > 1;

    const onModeChange = (currentMode: Modes) => {
        setGameSettings({ gameMode: currentMode });
        setMode(currentMode);
    };

    // initialize game id and mode
    useEffect(() => {
        setGameSettings({ gameId: game.game_id, gameMode: mode });
    }, [game, mode, setGameSettings]);

    useEffect(() => {
        if (!hasVersusStakes && selectedStake && selectedStake.currency !== stakeCurrency) {
            setGameSettings({ selectedStake: null });
        }
    }, [hasVersusStakes, selectedStake, stakeCurrency, setGameSettings]);

    const shouldShowBetrBucksToggle = betrBucks > 0;

    // Handle content overflow for small devices
    const [contentHeight, setContentHeight] = useState(0);
    const { height: screenHeight } = useWindowDimensions();
    const footerHeight = bottom + PLAY_BAR_HEIGHT + BOTTOM_TABS_HEIGHT;
    const scrollEnabled = contentHeight >= screenHeight - footerHeight;

    return (
        <ScrollView scrollEnabled={scrollEnabled}>
            <Box onLayout={event => setContentHeight(event.nativeEvent.layout.height)}>
                <Box height={HEADER_HEIGHT + top}>
                    <ImageBackground source={{ uri: game.metadata.banner }} style={styles.image}>
                        <Row justifyContent="space-between" paddingHorizontal="s16" style={{ paddingTop: top + 6 }}>
                            <BackButton />
                            <CashBalanceButton />
                            <InfoButton howToPlayUid={game.metadata.how_to_play_uid} />
                        </Row>
                        <LinearGradient
                            colors={[designSystem.colors.neutral900, 'rgba(16, 17, 20, 0.00)']}
                            style={styles.gradient}
                            angle={180}
                        />
                        <Row justifyContent="center" px="s16" style={styles.modeSwitchContainer}>
                            <ModeSwitch activeMode={mode} onModeChange={onModeChange} />
                        </Row>
                    </ImageBackground>
                </Box>
                {mode === '1v1' ? (
                    <Box justifyContent="center" px="s16" alignItems="center">
                        {shouldShowBetrBucksToggle ? (
                            <Box height={60} justifyContent="center">
                                <Row alignItems="center" gap="s12">
                                    <Text variant="bodyMedium" color="neutral300">
                                        {t('use_betr_bucks')} {toLocaleCurrency(betrBucks)}
                                    </Text>
                                    <BetrBucksToggle />
                                </Row>
                            </Box>
                        ) : null}
                        {hasVersusStakes ? (
                            <Box mt={!shouldShowBetrBucksToggle ? 's20' : 's0'}>
                                <EntryRadialSlider key={stakeCurrency} stakes={stakes} />
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
                {mode === 'practice' ? (
                    <Box justifyContent="center" alignItems="center" mt="s56">
                        <Box
                            borderWidth={1}
                            style={styles.practiceIconContainer}
                            height={56}
                            width={56}
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="r32"
                            backgroundColor={ds.colorToken.background.neutral.subtler}
                        >
                            <PracticeIcon />
                        </Box>
                        <Box mt="s12" px="s40" alignItems="center" gap="s4">
                            <Text variant="titleLarge" textAlign="center">
                                {t('practice_title')}
                            </Text>
                            <Text variant="bodyMedium" textAlign="center" color="neutral300">
                                {t('practice_description')}
                            </Text>
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    image: {
        width: '100%',
        height: '100%',
    },
    shadow: {
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 4,
    },
    modeSwitchContainer: {
        paddingTop: 126,
    },
    gradient: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: HEADER_HEIGHT / 2,
    },
    practiceIconContainer: {
        borderColor: ds.colorValue.border.subtle,
    },
});
